import { clearMarketingCache } from '../features/amazonAds/api/marketingCache';
import { clearCachedDataset } from '../features/spi/api/spiDatasetCache';

import { logger } from './logger';

/**
 * Deletes every IndexedDB database on this origin. Matches Vue's
 * `clearAllIndexedDB()` (`RW-ERP-Service/src/composables/mastery/mastery-store-db.ts:661-684`)
 * exactly — enumerate via `indexedDB.databases()`, delete each by name.
 * This is what actually covers the SHARED `LocalCache` database (used by
 * `stores/metadata-store.ts`, written by both this app and Vue) — deleting
 * it here means a signed-out user's cached metadata/master-list data
 * doesn't survive into the next sign-in on the same browser, same as Vue.
 *
 * Best-effort per database: one failing `deleteDatabase` call never blocks
 * the others, mirroring the individual try/resolve-on-error pattern the
 * Vue helper uses (it resolves rather than rejects per-database, so a
 * single stuck DB doesn't stop the rest).
 */
async function clearAllIndexedDb(): Promise<void> {
  if (typeof indexedDB === 'undefined' || typeof indexedDB.databases !== 'function') {
    return;
  }
  const databases = await indexedDB.databases();
  await Promise.all(
    databases.map(
      (db) =>
        new Promise<void>((resolve) => {
          if (!db.name) {
            resolve();
            return;
          }
          const request = indexedDB.deleteDatabase(db.name);
          request.onsuccess = () => resolve();
          request.onerror = () => {
            logger.warn(`[auth] failed to delete IndexedDB database '${db.name}' (non-fatal)`);
            resolve();
          };
          request.onblocked = () => resolve();
        }),
    ),
  );
}

/**
 * Wipe all PERSISTENT client-side data caches (IndexedDB) on logout, so a signed-out user — or a
 * different user who signs in next on the same browser — never sees the previous session's cached
 * business data. Best-effort and fully isolated: one cache failing to clear never blocks logout or
 * the others.
 *
 * `clearAllIndexedDb()` (delete every database) supersedes `clearCachedDataset()` (delete one
 * record from the `rw-spi` store) and `clearMarketingCache()` (empty the `MarketingCache` store)
 * in practice, but both stay as defensive fallbacks for browsers where
 * `indexedDB.databases()` is unavailable (Safari < 14, some webviews) and the blanket sweep
 * therefore does nothing at all.
 *
 * The Marketing screen's cache is NOT exempt from any of this: its entries hold revenue and COGS
 * figures, so nothing about it may outlive the session that fetched it.
 */
export async function clearAppCaches(): Promise<void> {
  const results = await Promise.allSettled([
    clearCachedDataset(),
    clearMarketingCache(),
    clearAllIndexedDb(),
  ]);
  const failed = results.filter((r) => r.status === 'rejected').length;
  if (failed > 0) {
    logger.warn(`[auth] ${failed} app cache(s) failed to clear on logout (non-fatal)`);
  }
}
